import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ExternalLink, Calendar, MapPin, Users } from "lucide-react";
import { toast } from "sonner";

function formatWhen(iso) {
  if (!iso) return "TBD";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " · " +
      d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminMeetups() {
  const [meetups, setMeetups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/meetups");
      setMeetups(data || []);
    } catch (e) {
      toast.error("Failed to load meetups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? Registrants stay in the DB but the page goes away.`)) return;
    try {
      await api.delete(`/admin/meetups/${id}`);
      toast.success("Meetup deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-meetups-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display uppercase text-4xl text-white">Community Meetups</h1>
          <p className="text-zinc-400 text-sm mt-1">Manage public events, registrations, and broadcasts.</p>
        </div>
        <Link to="/admin/meetups/new">
          <Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="admin-meetups-new">
            <Plus className="h-4 w-4 mr-2" /> New meetup
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : meetups.length === 0 ? (
        <div className="border border-[#27272A] bg-[#0F0F0F] p-10 text-center">
          <h2 className="font-display uppercase text-xl text-white mb-2">No meetups yet</h2>
          <p className="text-zinc-400 text-sm mb-6">Create your first community event — free or paid.</p>
          <Link to="/admin/meetups/new">
            <Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm">
              <Plus className="h-4 w-4 mr-2" /> Create meetup
            </Button>
          </Link>
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#0F0F0F] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="p-4">Event</th>
                <th className="p-4">When</th>
                <th className="p-4">Where</th>
                <th className="p-4">Price</th>
                <th className="p-4">Registered</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {meetups.map((m) => {
                const free = !m.price_cents || m.price_cents === 0;
                return (
                  <tr key={m.id} className="border-b border-[#27272A] hover:bg-[#121212]" data-testid={`admin-meetup-row-${m.slug}`}>
                    <td className="p-4">
                      <div className="font-bold text-white">{m.title}</div>
                      {m.subtitle && <div className="text-xs text-zinc-500 mt-0.5">{m.subtitle}</div>}
                      <div className="text-[10px] text-zinc-600 mt-1">/{m.slug}</div>
                    </td>
                    <td className="p-4 text-zinc-300 text-xs"><Calendar className="h-3 w-3 inline mr-1 text-[#FF331F]" />{formatWhen(m.starts_at)}</td>
                    <td className="p-4 text-zinc-300 text-xs"><MapPin className="h-3 w-3 inline mr-1 text-[#FF331F]" />{m.location_name}</td>
                    <td className="p-4 text-white font-bold">{free ? "Free" : `$${(m.price_cents / 100).toFixed(0)}`}</td>
                    <td className="p-4 text-zinc-300">
                      <Users className="h-3 w-3 inline mr-1 text-[#FF331F]" />
                      {m.registration_count || 0}{m.capacity ? ` / ${m.capacity}` : ""}
                    </td>
                    <td className="p-4">
                      {m.published
                        ? <span className="inline-block text-[10px] uppercase tracking-widest font-bold text-emerald-300 bg-emerald-950/40 border border-emerald-800 px-2 py-0.5">Published</span>
                        : <span className="inline-block text-[10px] uppercase tracking-widest font-bold text-amber-300 bg-amber-950/40 border border-amber-800 px-2 py-0.5">Draft</span>}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 justify-end">
                        <a href={`/meetups/${m.slug}`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white" data-testid={`admin-meetup-view-${m.slug}`}><ExternalLink className="h-4 w-4" /></a>
                        <Link to={`/admin/meetups/${m.id}`} className="text-zinc-400 hover:text-[#FF331F]" data-testid={`admin-meetup-edit-${m.slug}`}><Pencil className="h-4 w-4" /></Link>
                        <button onClick={() => remove(m.id, m.title)} className="text-zinc-400 hover:text-red-500" data-testid={`admin-meetup-delete-${m.slug}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
